import { DragBreakpoints } from 'interaction/DragBreakpoints';
import { DepartmentType } from 'app/data/Enums';
import { HomeViewConfig } from 'app/model/HomeViewConfig';
import { HomeDragDeps } from './home.types';

/**
 * Drag side of the home section: the DragBreakpoints wiring plus the
 * progress / velocity / breakpoint-index state. Owns the canonical
 * x progress value that the rest of the section reads each frame.
 */
export class HomeDrag {
	private _deps: HomeDragDeps;

	private _mainBreakpoints: DragBreakpoints;
	private _breakpointIndex: number = 0;
	private _progressX: number = 0;

	private _velocity: number = 0;
	private _frameProgress: number = 0;


	constructor(deps: HomeDragDeps) {
		this._deps = deps;
	}

	//Accessors
	//-------------------------------------------------------------------------------------

	public get isCreated(): boolean {
		return !!this._mainBreakpoints;
	}

	public get progress(): number {
		return this._progressX;
	}

	public get velocity(): number {
		return this._velocity;
	}

	public get breakpointIndex(): number {
		return this._breakpointIndex;
	}

	public set breakpointIndex(index: number) {
		this._breakpointIndex = index;
	}

	//DragBreakpoints delegation
	//-------------------------------------------------------------------------------------

	public setActive(enabled: boolean): void {
		if (this._mainBreakpoints) {
			this._mainBreakpoints.setActive(enabled);
		} else {
			console.warn('_mainBreakpoints not set');
		}
	}

	public setIndex(index: number): void {
		this._breakpointIndex = index;
		this._mainBreakpoints.setIndex(index);
	}

	public setVirtualWidth(virtualWidth: number): void {
		this._mainBreakpoints.setVirtualWidth(virtualWidth);
	}

	public setProgress(progress: number): void {
		this._progressX = progress;
		this._mainBreakpoints.setProgress(progress);
	}

	/** Reset the x progress state (breakpoints, velocity and index mapping) */
	public resetProgress(progress: number): void {
		this._mainBreakpoints.setProgress(progress);
		this._progressX = progress;
		this._velocity = 0;
		this._frameProgress = 0;

		if (progress === 0) {
			this._breakpointIndex = 0;
		} else if (progress === 1) {
			this._breakpointIndex = 1;
		} else {
			this._breakpointIndex = 2;
		}
	}

	//Creation
	//-------------------------------------------------------------------------------------

	public createBreakpoints(virtualWidth: number, json: HomeViewConfig, workDeeplinkDepartment: DepartmentType): void {
		let progress = 0;
		let startIndex = 0;

		if (workDeeplinkDepartment) {
			progress = workDeeplinkDepartment === DepartmentType.SCREENS ? 0 : 1;
			startIndex = workDeeplinkDepartment === DepartmentType.SCREENS ? 0 : 2;
			this._breakpointIndex = startIndex;
		}

		//Create the 0% / 50% / 100% for SCREENS <> HOME <> SPACES layout
		this._mainBreakpoints = new DragBreakpoints(4, false, startIndex, true);

		this._mainBreakpoints.setVirtualWidth(virtualWidth);
		this._mainBreakpoints.setDragToMoveRatio(json.dragToMoveRatio);
		this._mainBreakpoints.easePower = json.dragEasePower;
		this._mainBreakpoints.releaseEasePower = json.releaseEasePower;
		this._mainBreakpoints.forceSnap = false;

		this._progressX = progress;

		this._mainBreakpoints.setProgress(progress);

		this._mainBreakpoints.onTick = (progressRatio: number) => {
			this._frameProgress = progressRatio - this._progressX;
			this._progressX = progressRatio;
			this._deps.onFrame(progressRatio);
			this._effectVelocity(this._frameProgress);
		};

		this._mainBreakpoints.onStartDrag = () => {
			this._deps.onStartDrag();
		};

		this._mainBreakpoints.onStopDrag = () => {
			this._deps.onStopDrag();
		};
	}

	//Physics / snapping (DEAD: empty on the original controller, moved intact)
	//-------------------------------------------------------------------------------------


	private _effectVelocity(frameMoveRatio: number): void {}


}
